import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import useApi from 'api/apiProvider'
import { useKeys } from 'services/KeysProvider'
import { useCartStoreOrNull } from 'stores/cartStore/CartStoreProvider'
import { useAddressStore } from 'stores/RootStoreProvider'
import { usePricing } from '../../../components/Pricing/PricingProvider'
import { useCheckoutPromoInputs } from './useCheckoutPromoInputs'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { logger } from 'services/logger'
import { GroupCheckoutResponseSchema } from '../groupCheckoutPricing.schemas'
import { type GroupPricingResult } from '../groupCheckoutPricing.types'
import { calculateGroupCheckoutSummary } from '../../../components/Pricing/Pricing.api'
import {
  buildGroupOrders,
  buildGroupCheckoutSummaryParams,
} from '../../../components/Pricing/checkoutSummaryParams'
import { useCartDiscount } from './useCartDiscount'
import { useIsCartCheckout } from './useIsCartCheckout'

export const GROUP_PRICING_QUERY_KEY = 'group-pricing'

export function useGroupCheckoutPricing(): UseQueryResult<GroupPricingResult> {
  const api = useApi()
  const { ipCountry } = useKeys()
  const { pricingVariantsIds, discounts, pricingData } = usePricing()
  const cartStore = useCartStoreOrNull()
  const addressStore = useAddressStore()
  const isCartCheckout = useIsCartCheckout()

  const pricingPayloads = useMobxSelector(
    () => cartStore?.getPricingPayloads() ?? [],
    []
  )

  const discount = useCartDiscount()
  const discountId = discount?.metadata?.discountId

  const normalizedAddress = useMobxSelector(
    () => addressStore.currentNormalizedAddress,
    []
  )
  const { promocode, giftCodes, tileCredits } = useCheckoutPromoInputs()

  return useQuery({
    queryKey: [
      GROUP_PRICING_QUERY_KEY,
      discount,
      pricingVariantsIds,
      ipCountry,
      normalizedAddress,
      ...pricingPayloads,
      promocode,
      giftCodes,
      // Not sent as a parameter — the backend applies tile credits from the account.
      // Included in the key to invalidate the cache when credits change (e.g. free book gift card added).
      tileCredits,
    ],
    queryFn: async ({ signal }) => {
      if (!cartStore || pricingPayloads.length === 0) {
        throw new Error(
          'No cart store or pricing payloads, query should not be enabled'
        )
      }

      const orders = buildGroupOrders(
        pricingPayloads,
        pricingVariantsIds,
        discountId
      )
      if (orders.length === 0) {
        throw new Error('No orders, query should not be enabled')
      }

      const checkoutAddress = normalizedAddress
        ? {
            country: normalizedAddress.country,
            state: normalizedAddress.state,
            city: normalizedAddress.city,
            street: normalizedAddress.streetAddress,
            zipCode: normalizedAddress.zip,
            fullName: normalizedAddress.fullName,
          }
        : undefined

      try {
        const rawResponse = await api.call(
          calculateGroupCheckoutSummary(
            buildGroupCheckoutSummaryParams({
              orders,
              pricingCountry: ipCountry,
              checkoutId: cartStore.groupCheckoutId,
              address: checkoutAddress,
              promocode,
              giftCodes,
            })
          )
        )

        const result = GroupCheckoutResponseSchema.parse(rawResponse)
        // Skip the write if react-query aborted this fetch (superseded, or cleared on order
        // complete via removeQueries) so a late response can't resurrect a stale checkoutId.
        if (!signal.aborted) {
          cartStore.groupCheckoutId = result.checkoutId
        }
        return result
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error
        }
        logger.error('Group checkout pricing failed', error, {
          tags: { feature: 'group-checkout-pricing' },
        })
        throw error
      }
    },
    // The pricing endpoint currently mutates the saved checkout — a cache hit skips that write.
    staleTime: 0,
    enabled: isCartCheckout && !discounts.isLoading && !pricingData.isLoading,
  })
}
